import React from 'react';

import {Link} from 'react-router';

import ElapsedTime from 'app/components/general/ElapsedTime';
import {normalizeSignoffData} from 'app/signoffs/utils';
import {gt} from 'app/utils/i18n';

import {PreviousVersionIndicator} from './PreviousVersionIndicator';

import {
  SignoffDocumentSerializedType,
  SignoffSerializedType,
} from 'sweet-types';

import type {NormalizedSignoffDataType} from 'app/signoffs/types';

type Props = {
  alignment: 'left' | 'right';
  previousVersionStyle: 'badge' | 'link-on-text' | 'hidden';
} & (
  | {
      signoffData?: never;
      signoff: SignoffSerializedType | SignoffDocumentSerializedType;
    }
  | {signoffData: NormalizedSignoffDataType; signoff?: never}
);

export function SignoffStatus({
  alignment,
  previousVersionStyle,
  ...props
}: Props): JSX.Element {
  /* Four possible states for sign off
   *  1. not signed off
   *  2. signed off
   *  3. signed off, more recent version requires sign off again
   *  4. signed off, more recent version doesn't require sign off
   */
  const signoffData =
    props.signoffData != null
      ? props.signoffData
      : normalizeSignoffData(props.signoff);

  if (signoffData.signedAt == null) {
    // 1. not signed off.
    return (
      <ElapsedTime
        wrapTooltip={(timestamp) => gt('Sent %(timestamp)s', {timestamp})}
        timestamp={signoffData.lastRequestSentAt}
        ContainerTag="span"
        wrapText={gt('Not signed')}
        className="text-danger"
      />
    );
  }
  const previousVersionText = (
    <u>
      <Link to={signoffData.signedVersionHTMLURL!} className="text-danger">
        {gt('previous version')}
      </Link>
    </u>
  );

  // 3. signed off, more recent version requires sign off again.
  if (!signoffData.isSignedOff) {
    return (
      <>
        <ElapsedTime
          wrapTooltip={(timestamp) => gt('Sent %(timestamp)s', {timestamp})}
          timestamp={signoffData.lastRequestSentAt}
          ContainerTag="span"
          wrapText={gt('Not signed.')}
          className="text-danger mr-1"
        />
        <ElapsedTime
          timestamp={signoffData.signedAt}
          ContainerTag="span"
          wrapTextFn={(ts) => gt('(Signed a %s %s)', previousVersionText, ts)}
          className="text-danger"
        />
      </>
    );
  }

  // 2. signed off, 4. signed off more recent version doesn't require sign off

  const checkIcon = <i className="fas fa-check" />;

  const showingPreviousVersionLinkOnText =
    signoffData.signedVersionIsCurrent === false &&
    previousVersionStyle === 'link-on-text';

  const text = (
    <ElapsedTime
      className="mr-1"
      timestamp={signoffData.signedAt}
      ContainerTag="span"
      wrapTextFn={(ts) =>
        showingPreviousVersionLinkOnText
          ? gt('Signed a %s %s', previousVersionText, ts)
          : gt('Signed %s', ts)
      }
    />
  );

  const [leftElement, rightElement] =
    alignment === 'left' ? [checkIcon, text] : [text, checkIcon];

  return (
    <>
      <span
        className={
          showingPreviousVersionLinkOnText ? 'text-info' : 'text-success'
        }
      >
        {leftElement} {rightElement}
      </span>
      {previousVersionStyle === 'badge' ? (
        <>
          {' '}
          <PreviousVersionIndicator signoffData={signoffData} />
        </>
      ) : null}
    </>
  );
}
